import { useAuth } from "@/src/contexts/auth.context";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { enabled, scopeKey, type FollowControl } from "./contract";
import * as api from "./service";
export function useFollowingScope() {
  const { user, orgId, isSwitching } = useAuth();
  const viewer = user?.itemId ?? "",
    org = orgId ?? "";
  const key = scopeKey(viewer, org);
  const eligibleScope =
    enabled && !!viewer && !!org && org !== "default" && !isSwitching;
  // A public bundle flag is not pilot enrollment. This private, server-owned
  // bit is read directly and asynchronously; All feed/posting never wait for it.
  const settings = useQuery({
    queryKey: [...key, "settings"],
    queryFn: () => api.getSettings(viewer, org),
    enabled: eligibleScope,
    staleTime: 30000,
    refetchInterval: 60000,
    retry: false,
    meta: { persist: false },
  });
  return {
    viewer,
    org,
    available:
      eligibleScope &&
      settings.data?.featureEnabled === true &&
      !settings.isError,
    key,
  };
}
export function useAuthorFollow(author: string) {
  const scope = useFollowingScope(),
    client = useQueryClient(),
    key = [...scope.key, "control", author];
  const control = useQuery({
    queryKey: key,
    queryFn: () => api.getControl(scope.viewer, scope.org, author),
    enabled: scope.available && !!author && author !== scope.viewer,
    staleTime: 15000,
    retry: false,
    meta: {
      persist: false,
    },
  });
  const state = useQuery({
    queryKey: [...scope.key, "state", author, control.data?.revision ?? ""],
    queryFn: () => api.readState(author),
    enabled: scope.available && !!control.data?.desiredFollowing,
    refetchInterval: 15000,
    retry: false,
    meta: {
      persist: false,
    },
  });
  const mutation = useMutation({
    mutationFn: (
      patch: Partial<
        Pick<
          FollowControl,
          "desiredFollowing" | "appNotificationsEnabled" | "digestEnabled"
        >
      >,
    ) =>
      api.saveControl(
        scope.viewer,
        scope.org,
        author,
        control.data ?? null,
        patch,
      ),
    onMutate: async () => {
      await client.cancelQueries({
        queryKey: key,
      });
      const previous = client.getQueryData<FollowControl | null>(key);
      return {
        previous,
      };
    },
    onSuccess: (value) => {
      client.setQueryData(key, value);
      client.setQueryData<FollowControl[]>(
        [...scope.key, "controls"],
        (old) => {
          if (!old) return old;
          const rest = old.filter((c) => c.authorId !== author);
          return value.desiredFollowing ? [...rest, value] : rest;
        },
      );
      client.invalidateQueries({
        queryKey: scope.key,
      });
    },
    onError: (_e, _p, ctx) => {
      client.setQueryData(key, ctx?.previous ?? null);
      void client.invalidateQueries({
        queryKey: scope.key,
      });
    },
    retry: false,
  });
  return {
    ...scope,
    control,
    mutation,
    state: state.data?.items.find((i) => i.authorId === author)?.state,
    following:
      mutation.isPending && mutation.variables?.desiredFollowing !== undefined
        ? mutation.variables.desiredFollowing
        : control.data?.desiredFollowing === true,
    ready: !control.isPending && !control.isError && !mutation.isPending,
  };
}
export function useFollowingList() {
  const s = useFollowingScope();
  return useInfiniteQuery({
    queryKey: [...s.key, "list"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.readList(pageParam),
    getNextPageParam: (p) => p.nextCursor ?? undefined,
    enabled: s.available,
    staleTime: 15000,
    retry: false,
    meta: {
      persist: false,
    },
  });
}
export function useFollowerInbox(unreadOnly = false, active = true) {
  const s = useFollowingScope();
  const query = useInfiniteQuery({
    queryKey: [...s.key, "inbox", unreadOnly],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.readInbox(pageParam, unreadOnly),
    getNextPageParam: (p) => p.nextCursor ?? undefined,
    enabled: s.available && active,
    staleTime: 15000,
    refetchInterval: 30000,
    retry: false,
    meta: {
      persist: false,
    },
  });
  return { ...query, data: s.available ? query.data : undefined };
}
export function useFollowerPreferences() {
  const s = useFollowingScope(),
    client = useQueryClient();
  const query = useQuery({
    queryKey: [...s.key, "settings"],
    queryFn: () => api.getSettings(s.viewer, s.org),
    enabled: s.available,
    staleTime: 30000,
    retry: false,
    meta: { persist: false },
  });
  const mutation = useMutation({
    mutationFn: (patch: {
      appNotificationsEnabled?: boolean;
      digestEnabled?: boolean;
    }) => api.saveSettings(s.viewer, s.org, query.data ?? null, patch),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: s.key,
      }),
    retry: false,
  });
  return {
    ...s,
    query,
    mutation,
  };
}
export function useMarkAllFollowersRead() {
  const s = useFollowingScope(),
    client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      s.available
        ? api.markAllFollowersRead(s.viewer, s.org)
        : Promise.resolve(),
    onSettled: () =>
      client.invalidateQueries({
        queryKey: s.key,
      }),
    retry: false,
  });
}
